"use client";

import { c as _c } from "react/compiler-runtime";
import { MobileVotingModal, VotingModal } from "@/components/voting";
import VotingModalButton from "@/components/voting/VotingModalButton";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import { areSameProfileIdentity } from "@/helpers/ProfileHelpers";
import type { ActiveDropState } from "@/types/dropInteractionTypes";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import { useCallback, useState } from "react";
import { useDropInteractionRules } from "@/hooks/drops/useDropInteractionRules";
import useIsMobileDevice from "@/hooks/isMobileDevice";
import useIsMobileScreen from "@/hooks/isMobileScreen";
import WaveDropActions from "../WaveDropActions";
import WaveDropMobileMenu from "../WaveDropMobileMenu";
import WaveDropAuthorPfp from "../WaveDropAuthorPfp";
import DropMinimalIdentityRow from "../DropMinimalIdentityRow";
import ParticipationDropContainer from "./ParticipationDropContainer";
import ParticipationDropHeader from "./ParticipationDropHeader";
import ParticipationDropContent from "./ParticipationDropContent";
import ParticipationDropMetadata from "./ParticipationDropMetadata";
import ParticipationDropFooter from "./ParticipationDropFooter";
import useIsTouchDevice from "@/hooks/useIsTouchDevice";
import ParticipationIdentityProfileCard from "./ParticipationIdentityProfileCard";
import { getParticipationIdentityProfile, getParticipationVisibleMetadata } from "./participationIdentityProfile.helpers";
import type { DropContentPresentation } from "../dropContentPresentation";
import type { DropIdentityMode, DropInteractionParams, DropLocation, DropTimestampLayout } from "../drop.types";
import { hasDropFooter } from "../drop.types";
interface OngoingParticipationDropProps {
  readonly drop: ExtendedDrop;
  readonly showWaveInfo: boolean;
  readonly activeDrop: ActiveDropState | null;
  readonly showReplyAndQuote: boolean;
  readonly location: DropLocation;
  readonly onReply: (param: DropInteractionParams) => void;
  readonly onQuoteClick: (drop: ApiDrop) => void;
  readonly onDropContentClick?: ((drop: ExtendedDrop) => void) | undefined;
  readonly footer?: React.ReactNode;
  readonly identityMode?: DropIdentityMode | undefined;
  readonly timestampLayout?: DropTimestampLayout | undefined;
  readonly showInteractions?: boolean | undefined;
  readonly contentPresentation?: DropContentPresentation | undefined;
  readonly embedPath?: readonly string[] | undefined;
  readonly quotePath?: readonly string[] | undefined;
  readonly embedDepth?: number | undefined;
  readonly maxEmbedDepth?: number | undefined;
}
export default function OngoingParticipationDrop(t0) {
  const $ = _c(96);
  const {
    drop,
    showWaveInfo,
    activeDrop,
    showReplyAndQuote,
    location,
    onReply,
    onQuoteClick,
    onDropContentClick,
    footer,
    identityMode: t1,
    timestampLayout: t2,
    showInteractions: t3,
    contentPresentation: t4,
    embedPath,
    quotePath,
    embedDepth,
    maxEmbedDepth
  } = t0;
  const identityMode = t1 === undefined ? "default" : t1;
  const timestampLayout = t2 === undefined ? "inline" : t2;
  const showInteractions = t3 === undefined ? true : t3;
  const contentPresentation = t4 === undefined ? "default" : t4;
  const isActiveDrop = activeDrop?.drop.id === drop.id;
  const {
    canShowVote
  } = useDropInteractionRules(drop);
  const isMobile = useIsMobileDevice();
  const isMobileScreen = useIsMobileScreen();
  const hasTouch = useIsTouchDevice() || isMobile;
  let identityProfile;
  let t5;
  let visibleMetadata;
  if ($[0] !== drop.author || $[1] !== drop.metadata || $[2] !== drop.wave) {
    identityProfile = getParticipationIdentityProfile({
      wave: drop.wave,
      metadata: drop.metadata
    });
    let t6;
    if ($[6] !== drop.metadata || $[7] !== drop.wave) {
      t6 = getParticipationVisibleMetadata({
        wave: drop.wave,
        metadata: drop.metadata
      });
      $[6] = drop.metadata;
      $[7] = drop.wave;
      $[8] = t6;
    } else {
      t6 = $[8];
    }
    visibleMetadata = t6;
    t5 = identityProfile ? areSameProfileIdentity({
      left: drop.author,
      right: identityProfile
    }) : false;
    $[0] = drop.author;
    $[1] = drop.metadata;
    $[2] = drop.wave;
    $[3] = identityProfile;
    $[4] = t5;
    $[5] = visibleMetadata;
  } else {
    identityProfile = $[3];
    t5 = $[4];
    visibleMetadata = $[5];
  }
  const isSelfNominee = t5;
  const showIdentity = identityMode !== "hidden";
  const [activePartIndex, setActivePartIndex] = useState(0);
  const [longPressTriggered, setLongPressTriggered] = useState(false);
  const [isSlideUp, setIsSlideUp] = useState(false);
  const [isVotingModalOpen, setIsVotingModalOpen] = useState(false);
  let t6;
  if ($[9] !== hasTouch || $[10] !== showInteractions) {
    t6 = () => {
      if (!showInteractions || !hasTouch) {
        return;
      }
      setLongPressTriggered(true);
      setIsSlideUp(true);
    };
    $[9] = hasTouch;
    $[10] = showInteractions;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const handleLongPress = t6;
  let t7;
  if ($[12] !== activePartIndex || $[13] !== drop || $[14] !== onReply) {
    t7 = () => {
      setIsSlideUp(false);
      onReply({
        drop,
        partId: drop.parts[activePartIndex]?.part_id
      });
    };
    $[12] = activePartIndex;
    $[13] = drop;
    $[14] = onReply;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  const handleOnReply = t7;
  let t8;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => {
      setIsSlideUp(false);
    };
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  const handleOnAddReaction = t8;
  let t9;
  if ($[17] !== canShowVote || $[18] !== drop || $[19] !== showInteractions) {
    t9 = canShowVote && showInteractions ? <VotingModalButton drop={drop} onClick={() => setIsVotingModalOpen(true)} /> : null;
    $[17] = canShowVote;
    $[18] = drop;
    $[19] = showInteractions;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  const voteAction = t9;
  let t10;
  if ($[21] !== activePartIndex || $[22] !== drop || $[23] !== handleOnReply || $[24] !== isMobile || $[25] !== showInteractions || $[26] !== showReplyAndQuote) {
    t10 = !isMobile && showInteractions && showReplyAndQuote && <WaveDropActions drop={drop} activePartIndex={activePartIndex} showVoting={false} onReply={handleOnReply} />;
    $[21] = activePartIndex;
    $[22] = drop;
    $[23] = handleOnReply;
    $[24] = isMobile;
    $[25] = showInteractions;
    $[26] = showReplyAndQuote;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  let t11;
  if ($[28] !== drop || $[29] !== showIdentity) {
    t11 = showIdentity && <WaveDropAuthorPfp drop={drop} />;
    $[28] = drop;
    $[29] = showIdentity;
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  let t12;
  if ($[31] !== drop || $[32] !== identityMode || $[33] !== showIdentity || $[34] !== showWaveInfo || $[35] !== timestampLayout) {
    t12 = showIdentity && (identityMode === "minimal" ? <DropMinimalIdentityRow drop={drop} timestampLayout={timestampLayout} /> : <ParticipationDropHeader drop={drop} showWaveInfo={showWaveInfo} timestampLayout={timestampLayout} />);
    $[31] = drop;
    $[32] = identityMode;
    $[33] = showIdentity;
    $[34] = showWaveInfo;
    $[35] = timestampLayout;
    $[36] = t12;
  } else {
    t12 = $[36];
  }
  let t13;
  if ($[37] !== activePartIndex || $[38] !== contentPresentation || $[39] !== drop || $[40] !== embedDepth || $[41] !== embedPath || $[42] !== handleLongPress || $[43] !== maxEmbedDepth || $[44] !== onDropContentClick || $[45] !== onQuoteClick || $[46] !== quotePath) {
    t13 = <ParticipationDropContent drop={drop} activePartIndex={activePartIndex} setActivePartIndex={setActivePartIndex} onLongPress={handleLongPress} onDropContentClick={onDropContentClick} onQuoteClick={onQuoteClick} setLongPressTriggered={setLongPressTriggered} isCompetitionDrop={true} contentPresentation={contentPresentation} embedPath={embedPath} quotePath={quotePath} embedDepth={embedDepth} maxEmbedDepth={maxEmbedDepth} />;
    $[37] = activePartIndex;
    $[38] = contentPresentation;
    $[39] = drop;
    $[40] = embedDepth;
    $[41] = embedPath;
    $[42] = handleLongPress;
    $[43] = maxEmbedDepth;
    $[44] = onDropContentClick;
    $[45] = onQuoteClick;
    $[46] = quotePath;
    $[47] = t13;
  } else {
    t13 = $[47];
  }
  let t14;
  if ($[48] !== t12 || $[49] !== t13) {
    t14 = <div className="tw-flex tw-w-full tw-flex-col">{t12}{t13}</div>;
    $[48] = t12;
    $[49] = t13;
    $[50] = t14;
  } else {
    t14 = $[50];
  }
  let t15;
  if ($[51] !== t11 || $[52] !== t14) {
    t15 = <div className="tw-relative tw-z-10 tw-flex tw-w-full tw-gap-x-3 tw-border-0 tw-bg-transparent tw-px-4 tw-pt-4 tw-text-left">{t11}{t14}</div>;
    $[51] = t11;
    $[52] = t14;
    $[53] = t15;
  } else {
    t15 = $[53];
  }
  let t16;
  if ($[54] !== drop.id || $[55] !== identityProfile || $[56] !== isSelfNominee || $[57] !== showIdentity) {
    t16 = identityProfile && <div className={`${showIdentity ? "tw-ml-[3.25rem]" : ""} tw-px-4`}><ParticipationIdentityProfileCard profile={identityProfile} contextId={drop.id} variant="chat" showIdentityHeader={!isSelfNominee} /></div>;
    $[54] = drop.id;
    $[55] = identityProfile;
    $[56] = isSelfNominee;
    $[57] = showIdentity;
    $[58] = t16;
  } else {
    t16 = $[58];
  }
  let t17;
  if ($[59] !== drop.id || $[60] !== visibleMetadata) {
    t17 = <ParticipationDropMetadata metadata={visibleMetadata} contextId={drop.id} />;
    $[59] = drop.id;
    $[60] = visibleMetadata;
    $[61] = t17;
  } else {
    t17 = $[61];
  }
  let t18;
  if ($[62] !== drop || $[63] !== footer || $[64] !== showInteractions || $[65] !== voteAction) {
    t18 = (showInteractions || !hasDropFooter(footer)) && <ParticipationDropFooter drop={drop} voteAction={voteAction} showInteractions={showInteractions} />;
    $[62] = drop;
    $[63] = footer;
    $[64] = showInteractions;
    $[65] = voteAction;
    $[66] = t18;
  } else {
    t18 = $[66];
  }
  let t19;
  if ($[67] !== footer || $[68] !== showIdentity) {
    t19 = hasDropFooter(footer) && <div className={`${showIdentity ? "tw-ml-[3.25rem]" : ""} tw-px-4 tw-pb-4 tw-pt-2`}>{footer}</div>;
    $[67] = footer;
    $[68] = showIdentity;
    $[69] = t19;
  } else {
    t19 = $[69];
  }
  let t20;
  if ($[70] !== t16 || $[71] !== t17 || $[72] !== t18 || $[73] !== t19) {
    t20 = <div className="tw-flex tw-w-full tw-flex-col">{t16}{t17}{t18}{t19}</div>;
    $[70] = t16;
    $[71] = t17;
    $[72] = t18;
    $[73] = t19;
    $[74] = t20;
  } else {
    t20 = $[74];
  }
  let t21;
  if ($[75] !== drop || $[76] !== isMobileScreen || $[77] !== isVotingModalOpen || $[78] !== showInteractions) {
    t21 = showInteractions && (isMobileScreen ? <MobileVotingModal drop={drop} isOpen={isVotingModalOpen} onClose={() => setIsVotingModalOpen(false)} /> : <VotingModal drop={drop} isOpen={isVotingModalOpen} onClose={() => setIsVotingModalOpen(false)} />);
    $[75] = drop;
    $[76] = isMobileScreen;
    $[77] = isVotingModalOpen;
    $[78] = showInteractions;
    $[79] = t21;
  } else {
    t21 = $[79];
  }
  let t22;
  if ($[80] !== drop || $[81] !== handleOnReply || $[82] !== isSlideUp || $[83] !== longPressTriggered || $[84] !== showInteractions || $[85] !== showReplyAndQuote) {
    t22 = showInteractions && <WaveDropMobileMenu drop={drop} isOpen={isSlideUp} longPressTriggered={longPressTriggered} showReplyAndQuote={showReplyAndQuote} setOpen={setIsSlideUp} onReply={handleOnReply} onAddReaction={handleOnAddReaction} />;
    $[80] = drop;
    $[81] = handleOnReply;
    $[82] = isSlideUp;
    $[83] = longPressTriggered;
    $[84] = showInteractions;
    $[85] = showReplyAndQuote;
    $[86] = t22;
  } else {
    t22 = $[86];
  }
  let t23;
  if ($[87] !== drop || $[88] !== isActiveDrop || $[89] !== location || $[90] !== t10 || $[91] !== t15 || $[92] !== t20 || $[93] !== t21 || $[94] !== t22) {
    t23 = <ParticipationDropContainer drop={drop} isActiveDrop={isActiveDrop} location={location}>{t10}{t15}{t20}{t21}{t22}</ParticipationDropContainer>;
    $[87] = drop;
    $[88] = isActiveDrop;
    $[89] = location;
    $[90] = t10;
    $[91] = t15;
    $[92] = t20;
    $[93] = t21;
    $[94] = t22;
    $[95] = t23;
  } else {
    t23 = $[95];
  }
  return t23;
}